import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ImageUp, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAgent } from "@/hooks/useAgent";
import { useFileDrop } from "@/hooks/useFileDrop";
import { can } from "@/lib/roles";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { PropertyImage } from "@/components/agent/PropertyImage";
import { PropertyKeys } from "@/components/agent/PropertyKeys";
import { SettingsCard, SettingsControlRow } from "@/components/ui/settings-card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DangerButton, EmptyState, Field, GhostButton, ListingTag, Loading, PageHeader, PrimaryButton, SecondaryButton, errorCls, inputCls } from "@/components/agent/ui";

const MAX_BYTES = 500 * 1024;
const CRITERIA_KEYS = ["income_multiplier", "guarantor_income_multiplier", "payslip_months", "bank_net_tolerance_pct", "bank_pay_date_tolerance_days"];

const toForm = (l) => ({
  address: l.address, monthly_rent: l.monthly_rent, move_in_date: l.move_in_date, tenants: l.tenants, image_data: l.image_data || "",
  ...Object.fromEntries(CRITERIA_KEYS.map((k) => [k, l.criteria[k]])),
  flag_gambling: !!l.criteria.flag_gambling,
  accept_guarantor: l.criteria.accept_guarantor !== false,
  allow_pets: !!l.criteria.allow_pets,
});

const DeleteProperty = ({ letId, address }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/lets/${letId}`);
      toast.success("Property deleted");
      navigate("/properties");
    } catch (err) {
      toast.error(errorText(err));
      setBusy(false);
    }
  };
  return (
    <SettingsCard title="Delete property" description="Removes this property, its applicants and their documents for good." testid="editor-delete-card">
      <DangerButton className="w-fit" onClick={() => setOpen(true)} testid="editor-delete-button"><Trash2 /> Delete property</DangerButton>
      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent data-testid="delete-property-dialog">
          <DialogHeader>
            <DialogTitle>Delete {address}?</DialogTitle>
            <DialogDescription>Every application and document for this property is deleted too. This can't be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <SecondaryButton size="default" onClick={() => setOpen(false)} disabled={busy} testid="delete-property-cancel">Cancel</SecondaryButton>
            <DangerButton size="default" onClick={remove} isLoading={busy} testid="delete-property-confirm">Delete property</DangerButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsCard>
  );
};

const Photo = ({ src, onChange, disabled }) => {
  const input = useRef(null);
  const pick = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > MAX_BYTES) {
      toast.error("Use a JPG or PNG under 500KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  const drop = useFileDrop({ accept: "image/jpeg,image/png,image/webp", disabled, onFiles: ([f]) => pick(f), onReject: () => toast.error("Use a JPG or PNG under 500KB.") });
  return (
    <SettingsCard title="Photo" className="gap-3 p-1" testid="editor-photo-card">
      <div {...drop.bind} className="relative aspect-[4/3] w-full overflow-hidden rounded-lg" data-testid="editor-photo-drop">
        <PropertyImage src={src} testid="editor-photo" />
        {drop.dragging && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-brand bg-background/85 text-sm font-medium">
            <Upload className="size-5" /> Drop to upload your photo
          </div>
        )}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="editor-photo-input" />
      {!disabled && (
        <div className="flex gap-2 px-2 pb-2">
          <SecondaryButton onClick={() => input.current?.click()} testid="editor-change-photo"><ImageUp /> {src ? "Change photo" : "Upload photo"}</SecondaryButton>
          {src && <GhostButton onClick={() => onChange("")} testid="editor-clear-photo"><Trash2 /> Remove</GhostButton>}
        </div>
      )}
    </SettingsCard>
  );
};

const UnitInput = ({ unit, ...props }) => (
  <div className="relative">
    <input type="number" required className={`${inputCls} pr-16`} {...props} />
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{unit}</span>
  </div>
);

export default function PropertyEditor() {
  const { letId } = useParams();
  const agent = useAgent();
  const [let_, setLet] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [busy, setBusy] = useState(false);
  const editable = can(agent?.role, "edit");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/lets/${letId}`);
      setLet(data);
      setForm(toForm(data));
      setError("");
    } catch (err) {
      setError(errorText(err));
    }
  }, [letId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setSaveError("");
    try {
      const { data } = await api.patch(`/lets/${letId}`, {
        address: form.address, monthly_rent: Number(form.monthly_rent), move_in_date: form.move_in_date,
        tenants: Number(form.tenants), image_data: form.image_data,
        criteria: { ...Object.fromEntries(CRITERIA_KEYS.map((k) => [k, Number(form[k])])), flag_gambling: form.flag_gambling, accept_guarantor: form.accept_guarantor, allow_pets: form.allow_pets },
      });
      setLet(data);
      setForm(toForm(data));
      toast.success(`Saved · criteria version ${data.criteria_version}`);
    } catch (err) {
      setSaveError(errorText(err, "Please check the details and try again."));
    } finally {
      setBusy(false);
    }
  };

  if (error && !let_) return <AgentLayout wide><EmptyState title="Couldn't load this property" text={error} action={<SecondaryButton onClick={load} testid="editor-retry">Try again</SecondaryButton>} testid="editor-error" /></AgentLayout>;
  if (!form) return <AgentLayout wide><Loading testid="editor-loading" /></AgentLayout>;

  const saved = toForm(let_);
  const dirty = Object.keys(form).some((k) => String(form[k] ?? "") !== String(saved[k] ?? ""));

  const unit = (k, label, u, props = {}) => (
    <Field label={label}><UnitInput unit={u} value={form[k]} onChange={set(k)} disabled={!editable} data-testid={`editor-${k}`} {...props} /></Field>
  );

  return (
    <AgentLayout wide>
      <PageHeader back={{ to: "/properties", label: "Properties" }} title={let_.address}
        description={<span className="mt-1 flex items-center gap-2"><ListingTag status={let_.listing_status} testid="editor-listing" /><span data-testid="editor-criteria-version">Criteria version {let_.criteria_version}</span></span>}
        action={editable && dirty && <PrimaryButton type="submit" form="property-editor-form" isLoading={busy} testid="editor-save-button">Save changes</PrimaryButton>} />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-8">
          <Photo src={form.image_data} disabled={!editable} onChange={(v) => setForm({ ...form, image_data: v })} />
          <SettingsCard title="Links" description="Put the apply link on your listing." testid="editor-links">
            <PropertyKeys linkToken={let_.link_token} />
          </SettingsCard>
          {editable && <DeleteProperty letId={letId} address={let_.address} />}
        </div>
        <form id="property-editor-form" onSubmit={save} className="flex min-w-0 flex-col gap-8" data-testid="property-editor-form">
          <SettingsCard title="Details" testid="editor-details">
            <Field label="Address"><input required minLength={3} value={form.address} onChange={set("address")} disabled={!editable} className={inputCls} data-testid="editor-address" /></Field>
            <div className="grid gap-4 sm:grid-cols-3">
              {unit("monthly_rent", "Monthly rent", "£ / month", { min: 1, step: 1 })}
              <Field label="Move-in date"><input type="date" required value={form.move_in_date} onChange={set("move_in_date")} disabled={!editable} className={inputCls} data-testid="editor-move_in_date" /></Field>
              <Field label="Tenants"><input type="number" required min={1} max={10} value={form.tenants} onChange={set("tenants")} disabled={!editable} className={inputCls} data-testid="editor-tenants" /></Field>
            </div>
          </SettingsCard>
          <SettingsCard title="Criteria" description="Every applicant for this property is checked against exactly these rules." testid="editor-criteria">
            <div className="grid gap-4 sm:grid-cols-2">
              {unit("income_multiplier", "Minimum income", "× rent", { min: 1, step: 0.5 })}
              {form.accept_guarantor && unit("guarantor_income_multiplier", "Guarantor income", "× rent", { min: 1, step: 0.5 })}
              {unit("payslip_months", "Payslips required", "months", { min: 1, max: 12 })}
              {unit("bank_net_tolerance_pct", "Bank match tolerance", "%", { min: 0, step: 0.5 })}
              {unit("bank_pay_date_tolerance_days", "Pay date tolerance", "days", { min: 0 })}
            </div>
            <SettingsControlRow className="border-t pt-4" label="Accept a guarantor"
              description="Someone who agrees to pay the rent if the tenant can't. When off, applicants aren't asked for one."
              control={<Switch checked={form.accept_guarantor} disabled={!editable} onCheckedChange={(v) => setForm({ ...form, accept_guarantor: v })} data-testid="editor-accept-guarantor" />} />
            <SettingsControlRow className="border-t pt-4" label="Allow pets"
              description="When off, applicants are asked if they have pets, and anyone who does is flagged for you. It's never an automatic decline."
              control={<Switch checked={form.allow_pets} disabled={!editable} onCheckedChange={(v) => setForm({ ...form, allow_pets: v })} data-testid="editor-allow-pets" />} />
            <SettingsControlRow className="border-t pt-4" label="Flag gambling activity"
              description="Counts gambling and missed-payment fees in connected bank data. Only totals are shown, and it's never an automatic decline."
              control={<Switch checked={form.flag_gambling} disabled={!editable} onCheckedChange={(v) => setForm({ ...form, flag_gambling: v })} data-testid="editor-flag-gambling" />} />
            <div className="rounded-lg bg-panel p-3" data-testid="editor-criteria-lines">
              <p className="text-xs font-medium text-muted-foreground">What applicants see</p>
              <ul className="mt-1.5 flex flex-col gap-1">
                {let_.criteria_lines.map((line, i) => (
                  <li key={i} className="flex gap-2 text-sm"><span className="mt-2 size-1 shrink-0 rounded-full bg-foreground/40" />{line}</li>
                ))}
              </ul>
            </div>
          </SettingsCard>
          {saveError && <p className={errorCls} data-testid="editor-save-error">{saveError}</p>}
        </form>
      </div>
    </AgentLayout>
  );
}
